import { createContext, useCallback, useContext, useMemo, useEffect } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'

import { useQuery, useQueryClient } from 'react-query'

import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

import { dispatchAnalytics, gaEvents } from '@services/analytics'
import { loadFeatureFlags, loadKYC, loadUser } from '@services/hubii/hub-service'
import { LoadKYC } from '@services/hubii/hub-service/types'
import _ from 'lodash'

import HubiiLogo from '@assets/logo/hubii-main-blue.svg'

import { postLogout } from '@services/hubii/hub-service'

// Constantes
const FIRST_ACCESS_KEY = 'hub_first_access'
const AUTH_STORAGE_KEY = 'auth-storage'
const LOGOUT_REDIRECTPATH = '/entrar'
const DEFAULT_REDIRECT_PATH = '/pedidos'
const TUTORIAL_PATH = '/pedidos/tutorial'
const TOKEN_REFRESH_INTERVAL = 1000 * 60 // 1 minuto

// LocalStorage keys
const STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  HUB_UUID: 'hub_uuid',
  USER: 'user',
  TERM_UUID: 'term_uuid',
  AUTH_STORAGE: AUTH_STORAGE_KEY,
  firstAccess: (userUuid: string) => `${FIRST_ACCESS_KEY}_${userUuid}`,
}

// Chaves que devem ser preservadas durante o logout
const PRESERVED_KEYS = ['allow_to_audio']

const initialValues: AuthStoreContext.State = {
  user: null,
  selectedHub: null,
  accessToken: null,
  featureFlags: [],
}

namespace AuthStoreContext {
  export type State = {
    user: User | null
    selectedHub: Hub | null
    accessToken: string | null
    featureFlags: FeatureFlags
  }

  export type Actions = {
    setFeatureFlags: (featureFlags: FeatureFlags) => void
    setAccessToken: (accessToken: string) => void
    setUser: (user: User) => void
    setSelectedHub: (hubUuid: string) => void
    updateHub: (hub: Hub) => void
    reset: () => void
  }

  export type FeatureFlags = Array<{
    uuid: string
    name: string
  }>

  export type User = {
    uuid: string
    name: null | string
    surname: null | string
    email: string
    hubs: Array<Hub>
    show_tutorial?: boolean
  }

  export type Hub = {
    uuid: string
    name: string
    document: string
    trading_name: string
    address: {
      street_name: string
      address_number: string
      complement?: string
      postal_code: string
      neighborhood_name: string
      city_name: string
      province_code: string
    }
    operation_times: Array<{
      uuid: string
      weekday: number
      open: string
      close: string
      is_active: boolean
    }>
    pivot?: {
      user_role: {
        uuid: string
        name: string
      }
    }
  }
}

export const useAuthStore = create(
  persist<AuthStoreContext.State & AuthStoreContext.Actions>(
    (set, get) => ({
      ...initialValues,
      setFeatureFlags: (featureFlags: AuthStoreContext.FeatureFlags) => {
        set(() => ({ featureFlags }))
      },
      setAccessToken: (accessToken: string) => {
        localStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, accessToken)
        set(() => ({ accessToken }))
      },
      setUser: (user: AuthStoreContext.User) => {
        set(() => ({ user }))
      },
      setSelectedHub: (hubUuid: string) => {
        const selectedHub = get().user?.hubs.find((hub) => hub.uuid === hubUuid) ?? null
        if (selectedHub === null) return
        localStorage.setItem(STORAGE_KEYS.HUB_UUID, selectedHub.uuid)
        set(() => ({ selectedHub }))
      },
      updateHub: (hub: AuthStoreContext.Hub) => {
        const { user } = get()
        if (!user) return

        set(() => ({
          user: {
            ...user,
            hubs: user.hubs.map((_hub) => (_hub.uuid === hub.uuid ? hub : _hub)),
          },
        }))
      },
      reset: () => set(initialValues),
    }),
    {
      name: AUTH_STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),
    },
  ),
)

const AuthContext = createContext<AuthContextSpace.State>({
  kyc: null,
  kycError: null,
  setKyc: () => {},
  kycLoading: false,
  kycRefetch: () => {},
  accountStatus: 'Hub info not finished',
  hubOperationTimes: [],
  logout: () => {},
})

export const useAuth = () => useContext(AuthContext)

const AuthProvider = () => {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { selectedHub, setFeatureFlags, setSelectedHub, setUser } = useAuthStore()

  // Carrega o usuário
  const { status: userStatus } = useQuery({
    queryKey: 'user',
    queryFn: () => loadUser(),
    onSuccess: handleUserLoadSuccess,
    refetchInterval: TOKEN_REFRESH_INTERVAL,
  })

  // Função para lidar com o sucesso do carregamento do usuário
  function handleUserLoadSuccess(user: AuthStoreContext.User) {
    setUser(user)

    const storedHubUuid = localStorage.getItem(STORAGE_KEYS.HUB_UUID)
    let hubToSelect: string

    if (storedHubUuid && user.hubs.some((hub) => hub.uuid === storedHubUuid)) {
      hubToSelect = storedHubUuid
    } else {
      hubToSelect = selectedHub?.uuid || user.hubs[0].uuid
    }

    setSelectedHub(hubToSelect)

    // Necessário aguardar a atualização do state de seleção de hub
    setTimeout(() => {
      redirectUserBasedOnFirstAccess(user)
    }, 100)
  }

  // Função para redirecionamento baseado no primeiro acesso
  function redirectUserBasedOnFirstAccess(user: AuthStoreContext.User) {
    const shouldRedirectToTutorial = user?.show_tutorial
    if (shouldRedirectToTutorial) {
      navigate(TUTORIAL_PATH)
    } else {
      const currentPath = window.location.pathname
      if (currentPath === LOGOUT_REDIRECTPATH || currentPath === '/') {
        navigate(DEFAULT_REDIRECT_PATH)
      }
    }
  }

  // Invalida as queries quando o hub selecionado muda
  useEffect(() => {
    if (selectedHub?.uuid) {
      queryClient.invalidateQueries(['featureFlags', selectedHub.uuid])
      queryClient.invalidateQueries(['kyc', selectedHub.uuid])
    }
  }, [selectedHub?.uuid, queryClient])

  // Carrega as feature flags
  useQuery({
    queryKey: ['featureFlags', selectedHub?.uuid],
    queryFn: () => loadFeatureFlags(selectedHub?.uuid!),
    onSuccess: setFeatureFlags,
    enabled: !!selectedHub?.uuid,
  })

  // Carrega KYC
  const kycQuery = useQuery({
    queryKey: ['kyc', selectedHub?.uuid],
    queryFn: () => loadKYC(selectedHub?.uuid!),
    enabled: !!selectedHub?.uuid,
    retry: false,
  })

  const { data: kyc, isLoading: kycLoading, refetch: kycRefetch, error: kycError } = kycQuery

  // Define KYC
  const setKyc = useCallback(
    (data: LoadKYC.Response['data']) => {
      if (!selectedHub?.uuid) return
      queryClient.setQueryData(['kyc', selectedHub.uuid], data)
    },
    [queryClient, selectedHub],
  )

  // Função de logout
  const logout = useCallback(async () => {
    try {
      await postLogout()

      // Lista de chaves a serem removidas
      const keysToRemove = [
        STORAGE_KEYS.ACCESS_TOKEN,
        STORAGE_KEYS.HUB_UUID,
        STORAGE_KEYS.USER,
        STORAGE_KEYS.TERM_UUID,
        STORAGE_KEYS.AUTH_STORAGE,
      ]

      // Remove itens do localStorage, preservando as chaves especificadas
      keysToRemove.forEach((key) => {
        if (!PRESERVED_KEYS.includes(key)) {
          window.localStorage.removeItem(key)
        }
      })

      // Mantemos todos os valores de primeiro acesso (não os removemos)
      // Isso inclui chaves como `${FIRST_ACCESS_KEY}_${userUuid}`

      // Reseta o estado do Zustand
      useAuthStore.getState().reset()

      // Analytics de logout
      dispatchAnalytics(gaEvents.auth.logout())

      // Limpa cache e redireciona
      queryClient.removeQueries()
      queryClient.clear()
      navigate(LOGOUT_REDIRECTPATH)
    } catch (error) {
      console.error(error)
    }
  }, [navigate, queryClient])

  // Status da conta
  const accountStatus: AuthContextSpace.State['accountStatus'] = useMemo(() => {
    if (!selectedHub?.operation_times?.length || !selectedHub.trading_name)
      return 'Hub info not finished'

    switch (kyc?.status) {
      case LoadKYC.KYCStatus.Pending:
      case LoadKYC.KYCStatus.Finished:
        return 'KYC processing'
      case LoadKYC.KYCStatus.Error:
        return 'Rejected'
      case LoadKYC.KYCStatus.Approved:
        return 'Accepted'
      case LoadKYC.KYCStatus.Started:
      default:
        return 'KYC not filled'
    }
  }, [selectedHub, kyc])

  // Horários de funcionamento do hub
  const hubOperationTimes = useMemo(() => {
    return _(selectedHub?.operation_times || [])
      .groupBy((opTime) => opTime.weekday)
      .reduce<
        Array<{
          is_active: boolean
          weekday: number
          opening_hours: Array<{ open: string; close: string }>
        }>
      >((accumm, value) => {
        accumm.push({
          is_active: value[0]?.is_active,
          weekday: value[0]?.weekday,
          opening_hours: value.map((opTimeByWeekday) => ({
            close: opTimeByWeekday.close,
            open: opTimeByWeekday.open,
          })),
        })

        return accumm
      }, [])
  }, [selectedHub])

  // Valor do contexto
  const contextValue = useMemo(() => {
    return {
      kyc: kyc || null,
      setKyc,
      kycLoading,
      kycError,
      kycRefetch,

      hubOperationTimes,
      accountStatus,
      logout,
    }
  }, [kyc, kycLoading, kycError, hubOperationTimes, accountStatus, logout])

  return (
    <>
      {['loading'].includes(userStatus) && <LoadingState />}
      {['idle', 'success'].includes(userStatus) && (
        <AuthContext.Provider value={contextValue}>
          <Outlet />
        </AuthContext.Provider>
      )}
    </>
  )
}

export default AuthProvider

const LoadingState = () => (
  <div className='flex justify-center items-center h-screen'>
    <img
      src={HubiiLogo}
      alt='Hubii Logo'
      className='w-40 h-40 animate-fadeInOut animate-infinite animate-pulse'
    />
  </div>
)

namespace AuthContextSpace {
  export interface State {
    hubOperationTimes: Array<{
      is_active: boolean
      weekday: number
      opening_hours: Array<{
        open: string
        close: string
      }>
    }>

    kyc: LoadKYC.Response['data'] | null
    kycLoading: boolean
    kycError: unknown
    kycRefetch: () => void
    setKyc: (data: LoadKYC.Response['data']) => void

    accountStatus:
      | 'Hub info not finished'
      | 'KYC not filled'
      | 'KYC processing'
      | 'Rejected'
      | 'Accepted'

    logout: () => void
  }
}
